/**
 * Layer Admin Explorer Configuration
 *
 * Configuration for the Layer Admin Explorer using ConfigurableBaseExplorer pattern.
 * Provides CRUD operations for layers in the database.
 */

import {
  BaseExplorerConfig,
  EntityServiceAdapter,
  FieldTransformer,
  QueryParams,
  QueryResult,
  ExplorerFeatureConfig,
  PresentationConfig,
} from '../../../types/baseExplorerConfig';
import { Layer, LayerFilters } from '../../../types/layer';
import { LayerService } from '../../../services/layerService';
import { faLayerGroup } from '@fortawesome/free-solid-svg-icons';
import { objectToSearchParams } from '../baseExplorer/objectToSearchParams';
import { paginateClientSide } from '../../../utils/pagination';

// Initialize the service
const layerService = new LayerService();

// Field Transformers
const typeTransformer: FieldTransformer = {
  transform: (value: any) => {
    const typeMap: Record<string, string> = {
      wms: 'WMS',
      wfs: 'WFS',
      tms: 'TMS',
      live: 'Live',
      geojson: 'GeoJSON',
    };
    return typeMap[value] || value;
  },
  reverseTransform: (value: any) => String(value || '').toLowerCase(),
};

const categoryTransformer: FieldTransformer = {
  transform: (value: any) => {
    const categoryMap: Record<string, string> = {
      backgrounds: 'Background',
      overlays: 'Overlay',
      live: 'Live Data',
    };
    return categoryMap[value] || value;
  },
  reverseTransform: (value: any) => String(value || '').toLowerCase(),
};

const enabledTransformer: FieldTransformer = {
  transform: (value: any) => (value ? 'Yes' : 'No'),
  reverseTransform: (value: any) => value === 'Yes' || value === true,
};

const tagsTransformer: FieldTransformer = {
  transform: (value: any) => {
    if (!value) return '';
    if (Array.isArray(value)) return value.join(', ');
    return String(value);
  },
  reverseTransform: (value: any) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    return String(value)
      .split(',')
      .map((tag) => tag.trim())
      .filter((tag) => tag.length > 0);
  },
};

const opacityTransformer: FieldTransformer = {
  transform: (value: any) => {
    const opacity = Number(value);
    if (isNaN(opacity)) return 'N/A';
    return `${Math.round(opacity * 100)}%`;
  },
  reverseTransform: (value: any) => {
    const numValue = Number(value);
    if (!isNaN(numValue)) return numValue;
    // Parse percentage string
    const match = String(value).match(/(\d+)%?/);
    if (match) {
      return Number(match[1]) / 100;
    }
    return 1.0;
  },
};

const zoomTransformer: FieldTransformer = {
  transform: (value: any) => String(value ?? 'N/A'),
  reverseTransform: (value: any) => Number(value) || 0,
};

// Backend supports filtering on these four fields plus search; every other
// column filter is applied client-side after fetch (every layer ships back
// in one ~90-row payload, so this is cheap).
const BACKEND_FILTER_FIELDS = new Set(['enabled', 'category', 'type', 'layer_group']);

/**
 * Translate CBE filter/search params into LayerFilters. Single source of
 * truth used by both fetchData (view) and buildExportParams (export).
 * Only forwards filters the backend understands; column-funnel filters on
 * other fields are handled by applyClientFilters().
 */
export function translateParams(params: QueryParams): LayerFilters {
  const filters: LayerFilters = {};

  if (params?.filters) {
    for (const filter of params.filters) {
      if (filter.field === 'enabled') {
        // FilterPanel reads `e.target.value` from a <select>, which always
        // produces a string. The dropdown options are JS booleans in the
        // config, so we need to accept both — otherwise the filter is
        // silently dropped and the table shows every row.
        if (typeof filter.value === 'boolean') {
          filters.enabled = filter.value;
        } else if (filter.value === 'true') {
          filters.enabled = true;
        } else if (filter.value === 'false') {
          filters.enabled = false;
        }
      }
      if (filter.field === 'category') filters.category = filter.value;
      if (filter.field === 'type') filters.type = filter.value;
      if (filter.field === 'layer_group') filters.layer_group = filter.value;
    }
  }

  if (params?.search?.term) {
    filters.search = params.search.term;
  }

  return filters;
}


/**
 * Resolve dotted field paths (e.g. `metadata.geoserverParentGroup`) against
 * a row. Returns undefined if any segment is missing.
 */
function getPath(row: any, field: string): any {
  if (!field.includes('.')) return row?.[field];
  return field.split('.').reduce((acc, part) => (acc == null ? acc : acc[part]), row);
}

/**
 * Apply column-funnel filters that the backend doesn't handle (anything
 * outside BACKEND_FILTER_FIELDS). Operators come from CBE.buildQueryParams:
 * 'eq' for equality, 'contains' for textSearch, 'gte'/'lte' for date ranges.
 */
function applyClientFilters(layers: Layer[], params: QueryParams): Layer[] {
  const clientFilters = (params?.filters || []).filter(
    (f) => !BACKEND_FILTER_FIELDS.has(f.field),
  );
  if (clientFilters.length === 0) return layers;

  return layers.filter((row: any) => {
    return clientFilters.every((f) => {
      const value = getPath(row, f.field);
      if (f.value == null || f.value === '') return true;

      switch (f.operator) {
        case 'contains': {
          // Arrays (e.g. tags) match if any element contains the term.
          const needle = String(f.value).toLowerCase();
          if (Array.isArray(value)) {
            return value.some((v) => String(v).toLowerCase().includes(needle));
          }
          return value != null && String(value).toLowerCase().includes(needle);
        }
        case 'gte': return value != null && value >= f.value;
        case 'lte': return value != null && value <= f.value;
        case 'eq':
        default:
          if (typeof f.value === 'boolean') return value === f.value;
          return String(value) === String(f.value);
      }
    });
  });
}

/**
 * #693: build the export payload the SAME way the table narrows its rows —
 * apply the client-side column-funnel filters (name/tags/metadata/zoom/url/
 * date-range), then strip DB-managed fields so the JSON round-trips through
 * Bulk Import. Previously the export skipped applyClientFilters and dumped the
 * unfiltered backend result.
 */
export function buildExportableLayers(layers: Layer[], params: QueryParams): Partial<Layer>[] {
  return applyClientFilters(layers, params).map((l: any) => {
    const { id: _id, created_at: _ca, updated_at: _ua, default_order: _do, ...rest } = l;
    return rest;
  });
}

// Service Adapter
export const layerAdminServiceAdapter: EntityServiceAdapter<any> = {
  async fetchData(params: QueryParams): Promise<QueryResult<Layer>> {
    console.log('[LayerAdminExplorer] fetchData called with params:', params);

    const filters = translateParams(params);
    const backendLayers = await layerService.getAllLayers(filters);
    // Apply column-funnel filters the backend doesn't handle (name, tags,
    // metadata.*, zoom_*, default_*, source_url, legend_url, …).
    const layers = applyClientFilters(backendLayers, params);

    // Sort if needed
    if (params?.sort && params.sort.length > 0) {
      const sortParam = params.sort[0];
      layers.sort((a: any, b: any) => {
        const aVal = a[sortParam.field];
        const bVal = b[sortParam.field];

        if (aVal === bVal) return 0;
        if (aVal === null || aVal === undefined) return 1;
        if (bVal === null || bVal === undefined) return -1;

        const comparison = aVal < bVal ? -1 : 1;
        return sortParam.direction === 'asc' ? comparison : -comparison;
      });
    }

    return paginateClientSide(layers, params?.page, params?.limit);
  },

  async createEntity(data: any): Promise<any> {
    console.log('[LayerAdminExplorer] createEntity called with data:', data);
    return await layerService.createLayer(data);
  },

  async updateEntity(id: string, data: any): Promise<any> {
    console.log('[LayerAdminExplorer] updateEntity called:', id, data);
    return await layerService.updateLayer(id, data);
  },

  async deleteEntity(id: string): Promise<boolean> {
    console.log('[LayerAdminExplorer] deleteEntity called:', id);
    return await layerService.deleteLayer(id);
  },

  buildExportParams: (params: QueryParams) => objectToSearchParams(translateParams(params)),
};

// Feature Configuration
const features: ExplorerFeatureConfig = {
  pagination: {
    enabled: true,
    defaultPageSize: 25,
    pageSizeOptions: [25, 50, 100],
    showPageInfo: true,
  },
  sorting: {
    enabled: true,
    multiColumn: false,
    defaultSort: [{ field: 'category', direction: 'asc' }, { field: 'default_order', direction: 'asc' }],
  },
  filtering: {
    enabled: true,
    quickFilters: [
      {
        field: 'category',
        label: 'Category',
        type: 'dropdown',
        options: [
          { value: 'backgrounds', label: 'Backgrounds' },
          { value: 'overlays', label: 'Overlays' },
          { value: 'live', label: 'Live Data' },
        ],
      },
      {
        field: 'type',
        label: 'Type',
        type: 'dropdown',
        options: [
          { value: 'wms', label: 'WMS' },
          { value: 'wfs', label: 'WFS' },
          { value: 'tms', label: 'TMS' },
          { value: 'live', label: 'Live' },
          { value: 'geojson', label: 'GeoJSON' },
        ],
      },
      {
        field: 'enabled',
        label: 'Enabled',
        type: 'dropdown',
        options: [
          { value: true, label: 'Yes' },
          { value: false, label: 'No' },
        ],
      },
      { field: 'name', label: 'Layer Name', type: 'textSearch' },
      { field: 'layer_group', label: 'App Group', type: 'textSearch' },
      { field: 'metadata.geoserverParentGroup', label: 'GeoServer Group', type: 'textSearch' },
      { field: 'tags', label: 'Tags', type: 'textSearch' },
      { field: 'default_order', label: 'Order', type: 'numeric' },
      { field: 'default_opacity', label: 'Opacity', type: 'numeric' },
      { field: 'zoom_min', label: 'Min Zoom', type: 'numeric' },
      { field: 'zoom_max', label: 'Max Zoom', type: 'numeric' },
      { field: 'source_url', label: 'Source URL', type: 'textSearch' },
      { field: 'legend_url', label: 'Legend URL', type: 'textSearch' },
    ],
    advancedFilters: false,
  },
  searching: {
    enabled: true,
    placeholder: 'Search layers by name, group, or tags...',
    searchFields: ['name', 'layer_group', 'tags'],
    debounceMs: 300,
  },
  editing: {
    enabled: true,
    mode: 'modal',
    allowCreate: true,
    allowUpdate: true,
    allowDelete: true,
  },
  export: {
    enabled: true,
    formats: ['csv', 'excel'],
    includeFilters: true,
  },
};

// Presentation Configuration
const presentation: PresentationConfig = {
  title: 'Layer Administration',
  subtitle: 'Manage map layers for Layer Drawer 2.0',
  icon: faLayerGroup,

  columnConfig: [
    {
      field: 'name',
      title: 'Layer Name',
      width: 200,
      minWidth: 150,
      resizable: true,
      sortable: true,
      filterable: true,
      hidden: false,
    },
    {
      field: 'type',
      title: 'Type',
      width: 100,
      resizable: true,
      sortable: true,
      filterable: true,
      hidden: false,
      cellRenderer: {
        type: 'custom' as const,
        options: {
          render: (value: any, row: any) => {
            const typeMap: Record<string, string> = {
              wms: 'WMS',
              wfs: 'WFS',
              tms: 'TMS',
              live: 'Live',
              geojson: 'GeoJSON',
            };
            const typeStr = typeMap[value] || value;
            const isGSGroup = row?.metadata?.isGeoServerGroup;
            return isGSGroup ? `📁 ${typeStr}` : typeStr;
          }
        }
      },
    },
    {
      field: 'category',
      title: 'Category',
      width: 120,
      resizable: true,
      sortable: true,
      filterable: true,
      hidden: false,
    },
    {
      field: 'layer_group',
      title: 'App Group',
      width: 130,
      resizable: true,
      sortable: true,
      filterable: true,
      hidden: false,
    },
    {
      field: 'metadata.geoserverParentGroup',
      title: 'GeoServer Group',
      width: 150,
      resizable: true,
      sortable: false,
      filterable: true,
      hidden: false,
      cellRenderer: {
        type: 'custom' as const,
        options: {
          render: (value: any) => value || '-'
        }
      },
    },
    {
      field: 'tags',
      title: 'Tags',
      width: 150,
      resizable: true,
      sortable: false,
      filterable: true,
      hidden: false,
    },
    {
      field: 'enabled',
      title: 'Enabled',
      width: 80,
      resizable: false,
      sortable: true,
      filterable: true,
      hidden: false,
    },
    {
      field: 'default_order',
      title: 'Order',
      width: 80,
      resizable: false,
      sortable: true,
      filterable: true,
      hidden: false,
    },
    {
      field: 'default_opacity',
      title: 'Opacity',
      width: 90,
      resizable: false,
      sortable: true,
      filterable: true,
      hidden: false,
    },
    {
      field: 'zoom_min',
      title: 'Min Zoom',
      width: 90,
      resizable: false,
      sortable: true,
      filterable: true,
      hidden: false,
    },
    {
      field: 'zoom_max',
      title: 'Max Zoom',
      width: 90,
      resizable: false,
      sortable: true,
      filterable: true,
      hidden: false,
    },
    {
      field: 'source_url',
      title: 'Source URL',
      width: 200,
      resizable: true,
      sortable: false,
      filterable: true,
      hidden: true, // Hidden by default, user can show if needed
    },
    {
      field: 'legend_url',
      title: 'Legend URL',
      width: 200,
      resizable: true,
      sortable: false,
      filterable: true,
      hidden: true, // Hidden by default
    },
  ],
  layout: {
    minHeight: 400,
    responsive: true,
    showToolbar: true,
    showFooter: true,
  },
};

// Complete Configuration
export const layerAdminExplorerConfig: BaseExplorerConfig<any> = {
  entityName: 'Layer',
  complexityLevel: 'moderate',
  displayFields: [
    'name',
    'type',
    'category',
    'layer_group',
    'tags',
    'enabled',
    'default_order',
    'default_opacity',
    'zoom_min',
    'zoom_max',
  ],
  fieldTransformers: {
    type: typeTransformer,
    category: categoryTransformer,
    enabled: enabledTransformer,
    tags: tagsTransformer,
    default_opacity: opacityTransformer,
    zoom_min: zoomTransformer,
    zoom_max: zoomTransformer,
  } as any,
  serviceAdapter: layerAdminServiceAdapter as any,
  features,
  presentation,
};
